import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCreditCard,
    faUniversity,
    faUser,
    faSave,
    faSpinner,
    faEdit,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import { BankInfo } from '../../types';
import { useTranslation } from 'react-i18next';
import BankSelect from '../common/BankSelect';
import { findVietQrBank } from '../../utils/vietQr';

interface BankInfoFormProps {
    currentBankInfo: BankInfo | null;
    onSave: (bankInfo: BankInfo) => Promise<void>;
    onCancel?: () => void;
    loading?: boolean;
}

const BankInfoForm: React.FC<BankInfoFormProps> = ({
                                                       currentBankInfo,
                                                       onSave,
                                                       onCancel,
                                                       loading = false
                                                   }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState<BankInfo>({
        accountNumber: currentBankInfo?.accountNumber || '',
        bankName: currentBankInfo?.bankName || '',
        accountHolder: currentBankInfo?.accountHolder || ''
    });
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(!currentBankInfo);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.accountNumber || !formData.bankName || !formData.accountHolder) {
            alert(t('profile.components.bankInfoForm.errors.fillAllFields'));
            return;
        }

        // A bank outside the VietQR list has no BIN, so no transfer QR can be
        // built from it. Only a name saved before the picker can reach here.
        if (!findVietQrBank(formData.bankName)) {
            alert(t('profile.components.bankInfoForm.errors.pickBankFromList'));
            return;
        }

        setIsSubmitting(true);
        try {
            await onSave(formData);
            setIsEditing(false);
        } catch (error) {
            console.error('Error saving bank info:', error);
            alert(t('profile.components.bankInfoForm.errors.saveFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (currentBankInfo) {
            setFormData(currentBankInfo);
            setIsEditing(false);
        } else {
            onCancel && onCancel();
        }
    };

    // If showing as modal (when onCancel exists), wrap in modal structure
    if (onCancel) {
        return (
            <div className="glass-modal-overlay">
                <div className="glass-modal max-w-md w-full">
                    <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                                <FontAwesomeIcon icon={faCreditCard} className="mr-2 text-blue-600 dark:text-blue-400" />
                                {currentBankInfo ? t('profile.components.bankInfoForm.editTitle') : t('profile.components.bankInfoForm.addTitle')}
                            </h3>
                            <button
                                onClick={onCancel}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    <FontAwesomeIcon icon={faCreditCard} className="mr-2" />
                                    {t('profile.components.bankInfoForm.fields.accountNumber.labelRequired')}
                                </label>
                                <input
                                    type="text"
                                    value={formData.accountNumber}
                                    onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                    placeholder={t('profile.components.bankInfoForm.fields.accountNumber.placeholder')}
                                    required
                                    disabled={isSubmitting || loading}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    <FontAwesomeIcon icon={faUniversity} className="mr-2" />
                                    {t('profile.components.bankInfoForm.fields.bankName.labelRequired')}
                                </label>
                                <BankSelect
                                    value={formData.bankName}
                                    onChange={(bankName) => setFormData({ ...formData, bankName })}
                                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                    disabled={isSubmitting || loading}
                                    ariaLabel={t('profile.components.bankInfoForm.fields.bankName.label')}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    <FontAwesomeIcon icon={faUser} className="mr-2" />
                                    {t('profile.components.bankInfoForm.fields.accountHolder.labelRequired')}
                                </label>
                                <input
                                    type="text"
                                    value={formData.accountHolder}
                                    onChange={(e) => setFormData({ ...formData, accountHolder: e.target.value })}
                                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                    placeholder={t('profile.components.bankInfoForm.fields.accountHolder.placeholder')}
                                    required
                                    disabled={isSubmitting || loading}
                                />
                            </div>

                            <div className="flex space-x-3 pt-4">
                                <button
                                    type="submit"
                                    disabled={isSubmitting || loading}
                                    className="glass-btn-primary flex-1 flex items-center justify-center !py-2"
                                >
                                    {(isSubmitting || loading) ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                            {t('profile.components.bankInfoForm.buttons.saving')}
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSave} className="mr-2" />
                                            {t('profile.components.bankInfoForm.buttons.save')}
                                        </>
                                    )}
                                </button>

                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    disabled={isSubmitting || loading}
                                    className="glass-btn-ghost flex-1 flex items-center justify-center !py-2"
                                >
                                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                                    {t('profile.components.bankInfoForm.buttons.cancel')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    // Inline form for non-modal usage
    if (!isEditing && currentBankInfo) {
        return (
            <div className="card">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        <FontAwesomeIcon icon={faCreditCard} className="mr-2" />
                        {t('profile.components.bankInfoForm.title')}
                    </h3>
                    <button
                        onClick={() => setIsEditing(true)}
                        className="glass-btn-ghost !px-3 !py-1.5 text-sm"
                    >
                        <FontAwesomeIcon icon={faEdit} className="mr-2" />
                        {t('profile.components.bankInfoForm.buttons.edit')}
                    </button>
                </div>

                <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t('profile.components.bankInfoForm.fields.accountNumber.label')}
                        </label>
                        <p className="text-gray-900 dark:text-white">{currentBankInfo.accountNumber}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t('profile.components.bankInfoForm.fields.bankName.label')}
                        </label>
                        <p className="text-gray-900 dark:text-white">{currentBankInfo.bankName}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t('profile.components.bankInfoForm.fields.accountHolder.label')}
                        </label>
                        <p className="text-gray-900 dark:text-white">{currentBankInfo.accountHolder}</p>
                    </div>                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="flex items-center mb-4">
                <FontAwesomeIcon icon={faCreditCard} className="text-blue-600 dark:text-blue-400 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {currentBankInfo ? t('profile.components.bankInfoForm.editTitle') : t('profile.components.bankInfoForm.addTitle')}
                </h3>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <FontAwesomeIcon icon={faCreditCard} className="mr-2" />
                        {t('profile.components.bankInfoForm.fields.accountNumber.labelRequired')}
                    </label>
                    <input
                        type="text"
                        value={formData.accountNumber}
                        onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                        className="input-field"
                        placeholder={t('profile.components.bankInfoForm.fields.accountNumber.placeholder')}
                        required
                        disabled={isSubmitting || loading}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <FontAwesomeIcon icon={faUniversity} className="mr-2" />
                        {t('profile.components.bankInfoForm.fields.bankName.labelRequired')}
                    </label>
                    <BankSelect
                        value={formData.bankName}
                        onChange={(bankName) => setFormData({ ...formData, bankName })}
                        disabled={isSubmitting || loading}
                        ariaLabel={t('profile.components.bankInfoForm.fields.bankName.label')}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <FontAwesomeIcon icon={faUser} className="mr-2" />
                        {t('profile.components.bankInfoForm.fields.accountHolder.labelRequired')}
                    </label>
                    <input
                        type="text"
                        value={formData.accountHolder}
                        onChange={(e) => setFormData({ ...formData, accountHolder: e.target.value })}
                        className="input-field"
                        placeholder={t('profile.components.bankInfoForm.fields.accountHolder.placeholder')}
                        required
                        disabled={isSubmitting || loading}
                    />
                </div>

                <div className="flex space-x-3">
                    <button
                        type="submit"
                        disabled={isSubmitting || loading}
                        className="btn-primary flex items-center"
                    >
                        {(isSubmitting || loading) ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                {t('profile.components.bankInfoForm.buttons.saving')}
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} className="mr-2" />
                                {t('profile.components.bankInfoForm.buttons.save')}
                            </>
                        )}
                    </button>

                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isSubmitting || loading}
                        className="btn-secondary flex items-center"
                    >
                        <FontAwesomeIcon icon={faTimes} className="mr-2" />
                        {t('profile.components.bankInfoForm.buttons.cancel')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default BankInfoForm;